import React, { useState, useEffect, useMemo } from 'react';
import { UNIT_TO_G } from '../data/ingredients';
import {
  calcPricing, normalizePricing, defaultPricingSettings,
  CCFA_PRODUCER_CAP, CCFA_MEAT_START, SE_TAX_EFFECTIVE,
} from '../utils/pricing';
import shell  from './Tools.module.css';
import styles from './Pricing.module.css';

const STORAGE_KEY = 'sc_pricing';

// Rates and overhead belong to the operation, not to one recipe, so they are
// stored once and reused for every batch you price.
function loadPricing() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return normalizePricing(JSON.parse(raw));
  } catch (e) { /* fall through to defaults */ }
  return defaultPricingSettings();
}

const money = (n) => (Number(n) || 0).toLocaleString('en-US',
  { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const pct   = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function Pricing({ calc, ingDB, onClose }) {
  const [s, setS] = useState(loadPricing);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); }
    catch (e) { /* storage full or blocked — pricing still works in-session */ }
  }, [s]);

  const set = (key) => (value) => setS(prev => ({ ...prev, [key]: value }));
  const setNum = (key) => (e) => {
    const v = e.target.value;
    setS(prev => ({ ...prev, [key]: v === '' ? '' : parseFloat(v) }));
  };

  const { filledIngredients, casingLines, meatWeightG, totals, recipeName } = calc;
  const stuffableG = totals?.stuffable || 0;

  // ── Material cost for this batch: ingredients + casings ──────────────────
  const materialCost = useMemo(() => {
    let total = 0;
    (filledIngredients || []).forEach(ing => {
      const grams = (parseFloat(ing.pct) || 0) / 100 * meatWeightG;
      const cpg   = ingDB ? ingDB.costPerGram(ing.sectionKey, ing.name) : 0;
      total += cpg * grams;
    });
    (casingLines || []).forEach(line => {
      const feet  = parseFloat(line.feet) || 0;
      const entry = ingDB ? ingDB.getIngredient('casing', line.name) : null;
      if (!entry || !(entry.costPerUnit > 0)) return;
      total += feet * (entry.costPerUnit / (entry.costUnit === 'yd' ? 3 : 1));
    });
    return total;
  }, [filledIngredients, casingLines, meatWeightG, ingDB]);

  const unpriced = useMemo(() => (filledIngredients || []).filter(
    ing => !ingDB || ingDB.costPerGram(ing.sectionKey, ing.name) === 0
  ).length, [filledIngredients, ingDB]);

  const r = calcPricing(materialCost, stuffableG, s);

  // At home-batch scale, fixed costs per batch swamp the materials and push the
  // suggested price somewhere unsellable. Say so rather than quietly recommend it.
  const fixedShare = r.costPaid > 0 ? (r.laborCost + r.overhead) / r.costPaid : 0;
  const scaleWarning = r.finishedLb > 0 && fixedShare > 0.6;

  if (stuffableG <= 0) {
    return (
      <Shell onClose={onClose} recipeName={recipeName}>
        <div className={shell.section}>
          <Callout type="warn">
            Build a recipe first — pricing works from the stuffable weight and the
            ingredient costs of the batch you have loaded.
          </Callout>
        </div>
      </Shell>
    );
  }

  return (
    <Shell onClose={onClose} recipeName={recipeName}>
      <div className={styles.columns}>

        {/* ── LEFT: inputs ─────────────────────────────────────────── */}
        <div className={styles.inputCol}>

          <Group title="This batch">
            <ReadRow label="Ingredients + casings" value={money(materialCost)} />
            <ReadRow label="Stuffable weight" value={`${r.stuffLb.toFixed(2)} lb`} />
            {unpriced > 0 && (
              <p className={styles.warnNote}>
                {unpriced} ingredient{unpriced !== 1 ? 's have' : ' has'} no price set —
                the batch cost is understated. Set prices in 🗂️ Ingredients.
              </p>
            )}
          </Group>

          <Group title="Yield">
            <NumRow label="Cook / smoke loss" unit="%" hint="weight lost in the smoker"
              value={s.yieldLossPct} onChange={setNum('yieldLossPct')} />
            <ReadRow label="Finished saleable" value={`${r.finishedLb.toFixed(2)} lb`} strong />
          </Group>

          <Group title="Labor">
            <NumRow label="Hours per batch" unit="hrs" hint="prep, stuff, smoke, package, clean"
              value={s.laborHours} onChange={setNum('laborHours')} />
            <NumRow label="Your hourly rate" unit="$/hr"
              value={s.laborRate} onChange={setNum('laborRate')} />
            <ReadRow label="Labor cost" value={money(r.laborCost)} />
          </Group>

          <Group title="Overhead">
            <NumRow label="Monthly running costs" unit="$" hint="propane, wood, power, insurance, market fees"
              value={s.monthlyOverhead} onChange={setNum('monthlyOverhead')} />
            <NumRow label="Batches per month" unit=""
              value={s.batchesPerMonth} onChange={setNum('batchesPerMonth')} />
            <NumRow label="Annual registration" unit="$" hint="CDPHE — spread across the year"
              value={s.registrationFee} onChange={setNum('registrationFee')} />
            <ReadRow label="Overhead this batch" value={money(r.overhead)} />
          </Group>

          <Group title="Packaging">
            <NumRow label="Weight per package" unit="lb"
              value={s.packageWeightLb} onChange={setNum('packageWeightLb')} />
            <NumRow label="Cost per package" unit="$" hint="pouch, label, tie"
              value={s.packageCost} onChange={setNum('packageCost')} />
            <ReadRow label={`Packaging (${r.packages} pkg)`} value={money(r.pkgCost)} />
          </Group>

          <Group title="Price">
            <div className={shell.modeToggle}>
              <button
                className={`${shell.modeBtn} ${s.priceMode === 'margin' ? shell.modeBtnActive : ''}`}
                onClick={() => set('priceMode')('margin')}
              >Set a margin</button>
              <button
                className={`${shell.modeBtn} ${s.priceMode === 'price' ? shell.modeBtnActive : ''}`}
                onClick={() => set('priceMode')('price')}
              >Set the price</button>
            </div>
            {s.priceMode === 'margin' ? (
              <NumRow label="Target gross margin" unit="%" hint="on the selling price · 55–65% is typical"
                value={s.targetMarginPct} onChange={setNum('targetMarginPct')} />
            ) : (
              <NumRow label="Your price" unit="$/lb"
                value={s.manualPricePerLb} onChange={setNum('manualPricePerLb')} />
            )}
          </Group>

          <Group title="Tax set-aside">
            <NumRow label="Income tax held back" unit="%" hint="federal + Colorado, your bracket"
              value={s.taxSetAsidePct} onChange={setNum('taxSetAsidePct')} />
            <label className={styles.checkRow}>
              <input type="checkbox" checked={Boolean(s.applySeTax)}
                onChange={e => set('applySeTax')(e.target.checked)} />
              <span>Add self-employment tax ({(SE_TAX_EFFECTIVE * 100).toFixed(2)}% effective)</span>
            </label>
          </Group>

          <Group title="Cottage Foods cap">
            <NumRow label="Sales so far this year" unit="$"
              value={s.salesToDate} onChange={setNum('salesToDate')} />
          </Group>
        </div>

        {/* ── RIGHT: results ───────────────────────────────────────── */}
        <div className={styles.resultCol}>

          {scaleWarning && (
            <Callout type="warn">
              Labor and overhead are {pct(fixedShare * 100)} of this batch's cost.
              At {r.finishedLb.toFixed(1)} lb finished, fixed costs dominate and the
              suggested price will look unsellable. Bigger batches are the lever —
              the hours barely change when the batch doubles.
            </Callout>
          )}

          <div className={styles.priceHero}>
            <div className={styles.heroLabel}>
              {s.priceMode === 'margin' ? 'Suggested price' : 'Your price'}
            </div>
            <div className={styles.heroValue}>{money(r.pricePerLb)}<span>/lb</span></div>
            <div className={styles.heroSub}>
              {money(r.pricePerPackage)} per {s.packageWeightLb} lb package
              {' · '}{money(r.revenue)} for the batch
            </div>
          </div>

          {/* Labor paid vs labor free, side by side */}
          <div className={styles.compare}>
            <div className={styles.compareHead}>
              <span />
              <span className={styles.compareCol}>Paying yourself</span>
              <span className={styles.compareCol}>Time as profit</span>
            </div>
            <CompareRow label="Batch cost"
              a={money(r.costPaid)} b={money(r.costFree)} />
            <CompareRow label="Cost per finished lb"
              a={money(r.costPaidPerLb)} b={money(r.costFreePerLb)} strong />
            <CompareRow label="Break-even price / lb"
              a={money(r.breakEvenPaidPerLb)} b={money(r.breakEvenFreePerLb)} />
            <CompareRow label="Profit on this batch"
              a={money(r.profitPaid)} b={money(r.profitFree)} />
            <CompareRow label="Gross margin"
              a={pct(r.marginPaidPct)} b={pct(r.marginFreePct)} />
            <p className={styles.compareNote}>
              <strong>Paying yourself</strong> prices your hours in, so profit is what the
              operation earns on top of your wage. <strong>Time as profit</strong> leaves
              labor out, so the whole surplus is your take. The first tells you whether the
              price is sustainable; the second tells you what lands in your pocket.
            </p>
          </div>

          <div className={styles.block}>
            <div className={styles.blockTitle}>What you keep</div>
            <Row label="Take for the batch" value={money(r.profitFree)} />
            {s.applySeTax && <Row label="Self-employment tax" value={`− ${money(r.seTax)}`} />}
            <Row label={`Income tax (${s.taxSetAsidePct || 0}%)`} value={`− ${money(r.incomeTax)}`} />
            <Row label="After tax set-aside" value={money(r.takeHome)} strong />
            <p className={styles.blockNote}>
              Set-asides are computed on the labor-free take, since that is what
              reaches you as income. Move it to a separate account the day you're paid.
            </p>
          </div>

          <div className={styles.block}>
            <div className={styles.blockTitle}>Colorado Cottage Foods cap</div>
            <div className={styles.capTrack}>
              <div className={styles.capFill}
                   style={{ width: `${Math.min(100, r.capUsedPct)}%` }} />
            </div>
            <Row label="Sales to date" value={money(r.salesToDate)} />
            <Row label="Cap remaining" value={money(r.capRemaining)} />
            <Row label="Batches at this price" value={`${r.batchesToCap}`} strong />
            <p className={styles.blockNote}>
              The {CCFA_PRODUCER_CAP.toLocaleString('en-US',
                { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} per-producer
              cap and meat products both arrive with HB26-1033 on {CCFA_MEAT_START}.
              Until then sausage is outside the Cottage Foods Act — meat is excluded
              today. From that date meat must come from a federally or state-inspected
              facility, and you must register annually with CDPHE. Verify with CDPHE
              before your first sale; this is a planning tool, not legal advice.
            </p>
          </div>
        </div>
      </div>
    </Shell>
  );
}

/* ── Layout pieces ────────────────────────────────────────────── */

function Shell({ recipeName, onClose, children }) {
  return (
    <div className={shell.overlay}>
      <div className={shell.header}>
        <div className={shell.headerLeft}>
          <span className={shell.headerIcon}>💵</span>
          <div>
            <div className={shell.headerTitle}>Pricing</div>
            <div className={shell.headerSub}>
              {recipeName ? `${recipeName} — ` : ''}
              what the batch costs, and what to charge
            </div>
          </div>
        </div>
        <button className={shell.closeBtn} onClick={onClose}>✕ Close</button>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className={styles.group}>
      <div className={styles.groupTitle}>{title}</div>
      {children}
    </div>
  );
}

function NumRow({ label, unit, hint, value, onChange }) {
  return (
    <div className={shell.inputRow}>
      <label className={shell.inputLabel}>
        {label}
        {hint && <span className={shell.inputHint}>{hint}</span>}
      </label>
      <div className={shell.inputRight}>
        <input className={shell.numInput} type="number" step="any"
               value={value === '' || value === undefined ? '' : value}
               onChange={onChange} />
        {unit && <span className={shell.inputUnit}>{unit}</span>}
      </div>
    </div>
  );
}

function ReadRow({ label, value, strong }) {
  return (
    <div className={`${styles.readRow} ${strong ? styles.readStrong : ''}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className={`${styles.row} ${strong ? styles.rowStrong : ''}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function CompareRow({ label, a, b, strong }) {
  return (
    <div className={`${styles.compareRow} ${strong ? styles.compareStrong : ''}`}>
      <span className={styles.compareLabel}>{label}</span>
      <span className={styles.compareVal}>{a}</span>
      <span className={styles.compareVal}>{b}</span>
    </div>
  );
}

function Callout({ type, children }) {
  return (
    <div className={`${shell.callout} ${type === 'warn' ? shell.calloutWarn : shell.calloutInfo}`}>
      <span className={shell.calloutIcon}>{type === 'warn' ? '⚠️' : 'ℹ️'}</span>
      <div className={shell.calloutText}>{children}</div>
    </div>
  );
}
